import {
    createSeriesMarkers,
    type SeriesMarker,
    type SeriesMarkerShape,
    type Time,
} from 'lightweight-charts';
import { PatternPrimitive, type DrawPoint } from './PatternPrimitive';
import type { ApiPattern } from '../api/klines';

// ─── Конфігурація маркерів (прості свічкові патерни) ─────────────────────────

interface PatternStyle {
    color: string;
    position: 'aboveBar' | 'belowBar';
    shape: SeriesMarkerShape;
}

const PATTERN_STYLES: Record<string, PatternStyle> = {
    hammer:              { color: '#1976d2', position: 'belowBar', shape: 'arrowUp'   },
    shootingStar:        { color: '#e65100', position: 'aboveBar', shape: 'arrowDown' },
    doji:                { color: '#757575', position: 'aboveBar', shape: 'circle'    },
    bullishEngulfing:    { color: '#2e7d32', position: 'belowBar', shape: 'arrowUp'   },
    bearishEngulfing:    { color: '#c62828', position: 'aboveBar', shape: 'arrowDown' },
    darkCloud:           { color: '#6a1b9a', position: 'aboveBar', shape: 'arrowDown' },
    morningStar:         { color: '#00838f', position: 'belowBar', shape: 'arrowUp'   },
    eveningStar:         { color: '#e65100', position: 'aboveBar', shape: 'arrowDown' },
    threeWhiteSoldiers:  { color: '#1b5e20', position: 'belowBar', shape: 'arrowUp'   },
    threeBlackCrows:     { color: '#b71c1c', position: 'aboveBar', shape: 'arrowDown' },
    bullishHarami:       { color: '#0277bd', position: 'belowBar', shape: 'arrowUp'   },
    bearishHarami:       { color: '#6a1b9a', position: 'aboveBar', shape: 'arrowDown' },
    doubleTop:           { color: '#b71c1c', position: 'aboveBar', shape: 'arrowDown' },
    doubleBottom:        { color: '#1b5e20', position: 'belowBar', shape: 'arrowUp'   },
};

const DEFAULT_STYLE: PatternStyle = { color: '#757575', position: 'aboveBar', shape: 'circle' };

// ─── Рендерер ────────────────────────────────────────────────────────────────

export class PatternRenderer {
    private series: any;
    private chart: any;
    private enabledPatterns: Record<string, boolean>;

    private seriesMarkers: any = null;
    private primitives: PatternPrimitive[] = [];

    constructor(series: any, enabledPatterns: Record<string, boolean>, chart: any) {
        this.series = series;
        this.enabledPatterns = enabledPatterns;
        this.chart = chart;
    }

    /**
     * Рендерить патерни отримані з API.
     * Прості патерни → маркери (createSeriesMarkers).
     * Складні патерни (H&S, Triangle тощо) → PatternPrimitive (canvas).
     */
    render(apiPatterns: ApiPattern[]): void {
        const active = apiPatterns.filter(p => this.enabledPatterns[p.type]);
        this._renderMarkers(active.filter(p => !p.points?.length));
        active.filter(p => p.points?.length).forEach(p => this._renderComplexPattern(p));
    }

    private _toTime(isoStr: string): Time {
        return (new Date(isoStr).getTime() / 1000) as unknown as Time;
    }

    private _toPoint(p: { time: string; value: number }): DrawPoint {
        return { time: this._toTime(p.time), value: p.value };
    }

    // ── Прості патерни ────────────────────────────────────────────────────────

    private _renderMarkers(patterns: ApiPattern[]): void {
        const markers: SeriesMarker<Time>[] = patterns.map(p => {
            const style = PATTERN_STYLES[p.type] ?? DEFAULT_STYLE;
            return {
                time: this._toTime(p.time),
                position: style.position,
                color: style.color,
                shape: style.shape,
                text: p.type,
            };
        });
        this.seriesMarkers = createSeriesMarkers(this.series, markers);
    }

    // ── Складні патерни (canvas primitives) ──────────────────────────────────

    private _renderComplexPattern(pattern: ApiPattern): void {
        const points = pattern.points!;

        if (pattern.type === 'headAndShoulders') {
            this._drawHeadAndShoulders(points, false);
        } else if (pattern.type === 'inverseHeadAndShoulders') {
            this._drawHeadAndShoulders(points, true);
        } else if (pattern.type === 'triangle') {
            this._drawTriangle(points);
        } else if (pattern.type === 'bullishPennant') {
            this._drawPennant(points, false);
        } else if (pattern.type === 'bearishPennant') {
            this._drawPennant(points, true);
        } else if (pattern.type === 'fallingWedge') {
            this._drawWedge(points, false);
        } else if (pattern.type === 'risingWedge') {
            this._drawWedge(points, true);
        } else if (pattern.type === 'cupAndHandle') {
            this._drawCupAndHandle(points);
        } else if (pattern.type === 'ascendingTriangle') {
            this._drawAsymmetricTriangle(points, false);
        } else if (pattern.type === 'descendingTriangle') {
            this._drawAsymmetricTriangle(points, true);
        } else if (pattern.type === 'bullFlag') {
            this._drawFlag(points, false);
        } else if (pattern.type === 'bearFlag') {
            this._drawFlag(points, true);
        }
    }

    /**
     * Head and Shoulders / Inverse H&S
     * points: 5 shoulder points + 2 neckline points (7 total з бекенду)
     *
     * Малює:
     *   - Основна лінія: LS → valley1 → H → valley2 → RS
     *   - Neckline (пунктир): valley1 → valley2, трохи продовжена вправо
     *   - Кружечки на LS, H, RS
     *   - Підписи: "LS", "H", "RS", "Neckline"
     */
    private _drawHeadAndShoulders(
        rawPoints: ApiPattern['points'] & {},
        isInverse: boolean,
    ): void {
        const shoulder = rawPoints.filter(p => p.line === 'shoulder').map(p => this._toPoint(p));
        const neckline = rawPoints.filter(p => p.line === 'neckline').map(p => this._toPoint(p));

        if (shoulder.length < 5 || neckline.length < 2) return;

        const color = isInverse ? '#1b5e20' : '#7b1fa2';
        const neckColor = isInverse ? '#2e7d32' : '#b71c1c';
        const above = !isInverse;

        // Ключові точки: LS=[0], valley1=[1], H=[2], valley2=[3], RS=[4]
        const ls = shoulder[0];
        const h  = shoulder[2];
        const rs = shoulder[4];

        // Neckline трохи продовжена вправо: беремо rightmost neckline point
        // і додаємо до неї точку з тим самим value але +2 свічки часу (просто візуальний ефект)
        const nkRight = neckline[1];

        const dotPoints = [ls, h, rs];

        const labelOffY = above ? -18 : 18;
        const labels = [
            { point: ls, label: { text: 'LS',       color, offsetY: labelOffY, fontSize: 10 } },
            { point: h,  label: { text: isInverse ? 'H' : 'H', color, offsetY: labelOffY, fontSize: 10 } },
            { point: rs, label: { text: 'RS',       color, offsetY: labelOffY, fontSize: 10 } },
            { point: nkRight, label: { text: 'Neckline', color: neckColor, offsetY: above ? 12 : -12, fontSize: 9 } },
        ];

        this._attachPrimitive({
            patternPoints: shoulder,
            secondaryPoints: neckline,
            dotPoints,
            labels,
            patternColor: color,
            patternLineWidth: 2,
            secondaryColor: neckColor,
            secondaryDash: [8, 4],
            dotColor: color,
        });
    }

    /**
     * Triangle
     * points: 2 high points + 2 low points
     *
     * Малює:
     *   - Верхня лінія: high[0] → high[1]
     *   - Нижня лінія: low[0] → low[1]
     *   - Підпис "▽" в точці звуження
     */
    private _drawTriangle(rawPoints: ApiPattern['points'] & {}): void {
        const highPts = rawPoints.filter(p => p.line === 'high').map(p => this._toPoint(p));
        const lowPts  = rawPoints.filter(p => p.line === 'low').map(p => this._toPoint(p));

        if (highPts.length < 2 || lowPts.length < 2) return;

        const color = '#1565c0';

        // Апекс трикутника (правий кінець) — midpoint між правим high і low
        const apexValue = (highPts[1].value + lowPts[1].value) / 2;
        const apexPoint: DrawPoint = { time: highPts[1].time, value: apexValue };

        this._attachPrimitive({
            patternPoints: highPts,
            patternColor: color,
            patternLineWidth: 1.5,
            dotColor: color,
        });

        this._attachPrimitive({
            patternPoints: lowPts,
            patternColor: color,
            patternLineWidth: 1.5,
            labels: [
                { point: apexPoint, label: { text: '△ Triangle', color, offsetY: -14, fontSize: 10 } },
            ],
            dotColor: color,
        });
    }

    /**
     * Bullish / Bearish Pennant
     * points:
     *   line="flagpole" — 2 точки (основа → вершина флагштоку)
     *   line="high"     — 2 точки верхньої лінії вимпела
     *   line="low"      — 2 точки нижньої лінії вимпела
     *
     * Малює:
     *   - Флагшток: товста суцільна лінія
     *   - Вимпел: дві тонкі конвергуючі лінії (upper + lower)
     *   - Підпис "Pennant ↑" / "Pennant ↓" у вершині вимпела
     */
    private _drawPennant(rawPoints: ApiPattern['points'] & {}, isBearish: boolean): void {
        const polePts  = rawPoints.filter(p => p.line === 'flagpole').map(p => this._toPoint(p));
        const highPts  = rawPoints.filter(p => p.line === 'high').map(p => this._toPoint(p));
        const lowPts   = rawPoints.filter(p => p.line === 'low').map(p => this._toPoint(p));

        if (polePts.length < 2 || highPts.length < 2 || lowPts.length < 2) return;

        const color      = isBearish ? '#b71c1c' : '#1b5e20';
        const poleColor  = isBearish ? '#c62828' : '#2e7d32';

        // Апекс вимпела (правий кінець) — між верхньою і нижньою лінією
        const apexValue  = (highPts[1].value + lowPts[1].value) / 2;
        const apexPoint: DrawPoint = { time: highPts[1].time, value: apexValue };

        const labelText = isBearish ? 'Pennant ↓' : 'Pennant ↑';
        const labelOffY = isBearish ? 14 : -14;

        // 1. Флагшток — товста лінія
        this._attachPrimitive({
            patternPoints: polePts,
            patternColor: poleColor,
            patternLineWidth: 2.5,
            dotPoints: [polePts[1]],
            dotColor: poleColor,
        });

        // 2. Вимпел — верхня + нижня лінії
        this._attachPrimitive({
            patternPoints: highPts,
            secondaryPoints: lowPts,
            secondaryDash: [],           // суцільна (без пунктиру)
            patternColor: color,
            patternLineWidth: 1.5,
            secondaryColor: color,
            labels: [
                { point: apexPoint, label: { text: labelText, color, offsetY: labelOffY, fontSize: 10 } },
            ],
            dotColor: color,
        });
    }

    /**
     * Falling Wedge / Rising Wedge
     * points:
     *   line="high" — 2 точки верхньої лінії
     *   line="low"  — 2 точки нижньої лінії
     *
     * FallingWedge (isBearish=false): обидві лінії вниз, верхня крутіше → бичачий прорив
     * RisingWedge  (isBearish=true):  обидві лінії вгору, нижня крутіше → ведмежий прорив
     *
     * Малює:
     *   - Верхня лінія (solid)
     *   - Нижня лінія (solid) через secondaryPoints + secondaryDash:[]
     *   - Крапки на апексі (правий кінець)
     *   - Підпис
     */
    private _drawWedge(rawPoints: ApiPattern['points'] & {}, isBearish: boolean): void {
        const highPts = rawPoints.filter(p => p.line === 'high').map(p => this._toPoint(p));
        const lowPts  = rawPoints.filter(p => p.line === 'low').map(p => this._toPoint(p));

        if (highPts.length < 2 || lowPts.length < 2) return;

        const color     = isBearish ? '#c62828' : '#2e7d32';
        const labelText = isBearish ? '↓ Rising Wedge' : '↑ Falling Wedge';

        // Апекс клину (правий кінець) — між high і low
        const apexValue = (highPts[1].value + lowPts[1].value) / 2;
        const apexPoint: DrawPoint = { time: highPts[1].time, value: apexValue };
        const labelOffY = isBearish ? 14 : -14;

        this._attachPrimitive({
            patternPoints:   highPts,
            secondaryPoints: lowPts,
            secondaryDash:   [],            // суцільна нижня лінія
            patternColor:    color,
            patternLineWidth: 1.5,
            secondaryColor:  color,
            dotPoints:       [apexPoint],
            dotColor:        color,
            labels: [
                { point: apexPoint, label: { text: labelText, color, offsetY: labelOffY, fontSize: 10 } },
            ],
        });
    }

    /**
     * Cup and Handle (Чашка з ручкою)
     * points:
     *   line="cup"        — 3 точки V-форми (лівий обід → дно → правий обід)
     *   line="handle"     — 2 точки ручки (правий обід → кінець ручки)
     *   line="resistance" — 2 точки горизонтального рівня обода
     *
     * Малює:
     *   - Чашка: суцільна V-лінія
     *   - Ручка: суцільна лінія
     *   - Resistance: пунктирна горизонтальна лінія
     *   - Крапки на ключових точках
     *   - Підпис "Cup & Handle"
     */
    private _drawCupAndHandle(rawPoints: ApiPattern['points'] & {}): void {
        const cupPts        = rawPoints.filter(p => p.line === 'cup').map(p => this._toPoint(p));
        const handlePts     = rawPoints.filter(p => p.line === 'handle').map(p => this._toPoint(p));
        const resistancePts = rawPoints.filter(p => p.line === 'resistance').map(p => this._toPoint(p));

        if (cupPts.length < 3) return;

        const color     = '#0277bd';
        const resColor  = '#01579b';
        const labelPt   = resistancePts[1] ?? cupPts[2];

        // Чашка + ручка (одна безперервна лінія)
        const fullLine = handlePts.length >= 2
            ? [...cupPts, handlePts[1]]   // ліво→дно→право→кінець ручки
            : cupPts;

        this._attachPrimitive({
            patternPoints:   fullLine,
            tertiaryPoints:  resistancePts.length >= 2 ? resistancePts : undefined,
            tertiaryColor:   resColor,
            tertiaryDash:    [6, 4],
            patternColor:    color,
            patternLineWidth: 2,
            dotPoints:       [cupPts[0], cupPts[1], cupPts[2]],
            dotColor:        color,
            labels: [
                { point: labelPt, label: { text: '☕ Cup & Handle', color, offsetY: -14, fontSize: 10 } },
            ],
        });
    }

    /**
     * Ascending Triangle (isBearish=false) / Descending Triangle (isBearish=true)
     * points:
     *   line="resistance" — 2 точки верхньої лінії
     *   line="support"    — 2 точки нижньої лінії
     *
     * Ascending:   flat resistance (дашед) + rising support (solid зелений)
     * Descending:  falling resistance (solid червоний) + flat support (дашед)
     */
    private _drawAsymmetricTriangle(
        rawPoints: ApiPattern['points'] & {},
        isBearish: boolean,
    ): void {
        const resPts = rawPoints.filter(p => p.line === 'resistance').map(p => this._toPoint(p));
        const supPts = rawPoints.filter(p => p.line === 'support').map(p => this._toPoint(p));

        if (resPts.length < 2 || supPts.length < 2) return;

        const color     = isBearish ? '#c62828' : '#2e7d32';
        const flatColor = isBearish ? '#1565c0' : '#1565c0';
        const labelText = isBearish ? '↓ Descending △' : '↑ Ascending △';

        // Апекс (правий кінець де лінії перетинаються)
        const apexPt: DrawPoint = {
            time: resPts[1].time,
            value: (resPts[1].value + supPts[1].value) / 2,
        };

        if (isBearish) {
            // Descending: solid falling resistance + dashed flat support
            this._attachPrimitive({
                patternPoints:   resPts,
                secondaryPoints: supPts,
                secondaryDash:   [6, 4],
                patternColor:    color,
                patternLineWidth: 1.5,
                secondaryColor:  flatColor,
                dotPoints:       [apexPt],
                dotColor:        color,
                labels: [{ point: apexPt, label: { text: labelText, color, offsetY: 14, fontSize: 10 } }],
            });
        } else {
            // Ascending: dashed flat resistance + solid rising support
            this._attachPrimitive({
                patternPoints:   supPts,
                secondaryPoints: resPts,
                secondaryDash:   [6, 4],
                patternColor:    color,
                patternLineWidth: 1.5,
                secondaryColor:  flatColor,
                dotPoints:       [apexPt],
                dotColor:        color,
                labels: [{ point: apexPt, label: { text: labelText, color, offsetY: -14, fontSize: 10 } }],
            });
        }
    }

    /**
     * Bull Flag (isBearish=false) / Bear Flag (isBearish=true)
     * points:
     *   line="flagpole" — 2 точки флагштоку
     *   line="high"     — 2 точки верхньої лінії каналу
     *   line="low"      — 2 точки нижньої лінії каналу
     *
     * Канал паралельний (не конвергуючий) — основна відмінність від Pennant.
     * Малює: товстий флагшток + дві паралельні лінії каналу + підпис.
     */
    private _drawFlag(rawPoints: ApiPattern['points'] & {}, isBearish: boolean): void {
        const polePts = rawPoints.filter(p => p.line === 'flagpole').map(p => this._toPoint(p));
        const highPts = rawPoints.filter(p => p.line === 'high').map(p => this._toPoint(p));
        const lowPts  = rawPoints.filter(p => p.line === 'low').map(p => this._toPoint(p));

        if (polePts.length < 2 || highPts.length < 2 || lowPts.length < 2) return;

        const color      = isBearish ? '#b71c1c' : '#1b5e20';
        const poleColor  = isBearish ? '#c62828' : '#2e7d32';
        const labelText  = isBearish ? '🚩 Bear Flag' : '🚩 Bull Flag';
        const labelOffY  = isBearish ? 14 : -14;

        const midPt: DrawPoint = {
            time: highPts[1].time,
            value: (highPts[1].value + lowPts[1].value) / 2,
        };

        // Флагшток — товста лінія
        this._attachPrimitive({
            patternPoints:    polePts,
            patternColor:     poleColor,
            patternLineWidth: 2.5,
            dotPoints:        [polePts[1]],
            dotColor:         poleColor,
        });

        // Паралельний канал: верхня + нижня лінії (обидві solid)
        this._attachPrimitive({
            patternPoints:   highPts,
            secondaryPoints: lowPts,
            secondaryDash:   [],             // суцільна нижня лінія
            patternColor:    color,
            patternLineWidth: 1.5,
            secondaryColor:  color,
            labels: [{ point: midPt, label: { text: labelText, color, offsetY: labelOffY, fontSize: 10 } }],
            dotColor: color,
        });
    }

    private _attachPrimitive(opts: ConstructorParameters<typeof PatternPrimitive>[0]): void {
        const primitive = new PatternPrimitive(opts);
        this.series.attachPrimitive(primitive);
        this.primitives.push(primitive);
    }

    // ── Очищення ──────────────────────────────────────────────────────────────

    clear(): void {
        this.seriesMarkers?.setMarkers([]);
        for (const p of this.primitives) {
            try { this.series.detachPrimitive(p); } catch {}
        }
        this.primitives = [];
    }
}
